import { useRef } from "react";
import { useSubmitAnalyticMutation } from "redux/queries/analytics";

function generateSessionId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const getInitialData = (previousSessionId = null) => ({
  session_id: generateSessionId(),
  previous_session_id: previousSessionId,
  featureEntry: null,
  cameraOpened: null,
  frameSent: null,
  eligibleCount: 0,
  nonEligibleCount: 0,
  errorCount: 0,
  os: null,
  totalTimeSpent: null,
  nationality: null,
  gender: null,
  dateOfBirth: null,
  t_image_submitted: null,
  t_ocr_start: null,
  t_ocr_end: null,
  t_parse_start: null,
  t_parse_end: null,
  t_validation_start: null,
  t_validation_end: null,
  t_flow_end: null,
  // Subdocument: { http_status, fault_code, message, action }
  error: null,
  reason: null,
  detail: null,
  source: null,
  terminal_id: null,
  area: null,
  sub_area: null,
  sent: false,
});

const METHODS = [
  "markFeatureEntry",
  "markCameraOpened",
  "markFrameSent",
  "markImageSubmitted",
  "markFlowEnd",
  "markTotalTimeSpent",
  "setSource",
  "setOS",
  "setNationality",
  "setGender",
  "setDateOfBirth",
  "setBackendTimestamps",
  "setError",
  "incrementEligible",
  "incrementNonEligible",
  "incrementError",
  "submit",
  "chainNext",
];

export default function useScanAnalytics(areAnalyticsEnabled = true) {
  const [submitAnalytic] = useSubmitAnalyticMutation();
  const analyticsRef = useRef(getInitialData());

  if (!areAnalyticsEnabled) {
    const noop = () => {};
    return Object.fromEntries(METHODS.map((m) => [m, noop]));
  }

  function update(patch) {
    analyticsRef.current = { ...analyticsRef.current, ...patch };
  }

  function markFeatureEntry() {
    if (!analyticsRef.current.featureEntry)
      update({ featureEntry: Date.now() });
  }

  function markCameraOpened() {
    if (!analyticsRef.current.cameraOpened)
      update({ cameraOpened: Date.now() });
  }

  function markFrameSent() {
    update({ frameSent: Date.now() });
  }

  function markImageSubmitted() {
    if (!analyticsRef.current.t_image_submitted) {
      update({ t_image_submitted: Date.now() });
    }
  }

  function markFlowEnd() {
    if (!analyticsRef.current.t_flow_end) update({ t_flow_end: Date.now() });
  }

  function markTotalTimeSpent() {
    if (
      !analyticsRef.current.totalTimeSpent &&
      analyticsRef.current.frameSent
    ) {
      update({ totalTimeSpent: Date.now() - analyticsRef.current.frameSent });
    }
  }

  function setSource(source, terminalId = null, area = null, subArea = null) {
    update({
      source: source || null,
      terminal_id: terminalId || null,
      area: area || null,
      sub_area: subArea || null,
    });
  }

  function setOS(os) {
    update({ os });
  }

  function setNationality(nationality) {
    update({ nationality });
  }

  function setGender(gender) {
    update({ gender });
  }

  function setDateOfBirth(dateOfBirth) {
    update({ dateOfBirth });
  }

  function setBackendTimestamps(timestamps) {
    update({
      t_ocr_start: timestamps.t_ocr_start || null,
      t_ocr_end: timestamps.t_ocr_end || null,
      t_parse_start: timestamps.t_parse_start || null,
      t_parse_end: timestamps.t_parse_end || null,
      t_validation_start: timestamps.t_validation_start || null,
      t_validation_end: timestamps.t_validation_end || null,
    });
  }

  // Overwrites any previous error; errorCount tracks the total.
  function setError(errorData) {
    update({ error: errorData });
  }

  function incrementEligible() {
    update({ eligibleCount: (analyticsRef.current.eligibleCount || 0) + 1 });
  }

  function incrementNonEligible() {
    update({
      nonEligibleCount: (analyticsRef.current.nonEligibleCount || 0) + 1,
    });
  }

  function incrementError() {
    update({ errorCount: (analyticsRef.current.errorCount || 0) + 1 });
  }

  // Idempotent — whichever caller fires first wins, later callers no-op.
  // The validation response handler is the primary caller; the browser-exit
  // listeners and pre-response user exits act as a safety net.
  function submit(reason, detail = null) {
    if (analyticsRef.current.sent) return;
    markFlowEnd();
    update({ reason, detail, sent: true });
    const { sent: _sent, ...payload } = analyticsRef.current;
    submitAnalytic({ type: "scan_timing", ...payload });
  }

  // Starts a fresh session linked to the just-submitted one. Used by
  // Try Again, Scan Another, and the drawer's scan-another paths.
  function chainNext() {
    analyticsRef.current = getInitialData(analyticsRef.current.session_id);
  }

  return {
    markFeatureEntry,
    markCameraOpened,
    markFrameSent,
    markImageSubmitted,
    markFlowEnd,
    markTotalTimeSpent,
    setSource,
    setOS,
    setNationality,
    setGender,
    setDateOfBirth,
    setBackendTimestamps,
    setError,
    incrementEligible,
    incrementNonEligible,
    incrementError,
    submit,
    chainNext,
  };
}
